import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ShieldAlert, Loader2, Download, RefreshCw, ExternalLink, Link2, Mail,
  Printer, CheckCircle2, Clock, AlertTriangle, Search, ArrowLeft, DollarSign,
  Plus, Trash2, Tag, Archive, ArchiveRestore, FileDown, StickyNote, Landmark,
  ChevronDown, ChevronUp, Send,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) =>
  Number(n) ? "$" + Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 }) : "—";
const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return s; }
};

const STATUS_META = {
  intake: { label: "Awaiting payment", color: "#E4C97A", bg: "#2a2410", icon: Clock },
  engaged: { label: "Paid · engaged", color: "#6FD3AE", bg: "#0A241A", icon: DollarSign },
  delivered: { label: "Kit delivered", color: "#7ec6ff", bg: "#0e2233", icon: CheckCircle2 },
};

const PRESET_TAGS = ["Priority", "In progress", "Awaiting client", "Funds recovered", "Closed", "Referred to attorney"];
const PRESET_AGENCIES = ["FBI IC3", "FTC", "SEC", "FINRA", "CFPB", "IRS-CI", "U.S. Secret Service", "State Attorney General", "Local Police", "Bank fraud dept."];
const TAG_COLOR = {
  Priority: "#ff9b86", "In progress": "#7ec6ff", "Awaiting client": "#E4C97A",
  "Funds recovered": "#6FD3AE", Closed: "#9ec9ba", "Referred to attorney": "#c9a0ff",
};

export default function Investigations() {
  const nav = useNavigate();
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [busyId, setBusyId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const token = localStorage.getItem("th_admin_token");
  const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const r = await axios.get(`${API}/admin/fraud-report/cases?include_archived=${showArchived}`, cfg);
      setCases(r.data.cases || []);
      setStats(r.data.stats || null);
    } catch (e) {
      if (!silent) toast.error("Could not load investigations.");
    } finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => {
    load();
    localStorage.setItem("fciu_last_seen", String(Date.now())); // clears the admin "new case" badge
    const iv = setInterval(() => load(true), 20000); // live refresh
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [showArchived]);

  const patchCase = (row) => setCases((prev) => {
    if (row.archived && !showArchived) return prev.filter((x) => x.id !== row.id);
    return prev.map((x) => (x.id === row.id ? row : x));
  });

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/admin/fraud-report/cases.csv`, { ...cfg, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `fciu_investigations_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success("CSV exported");
    } catch { toast.error("Export failed"); }
  };

  const addNote = async (c, text, clear) => {
    if (!text.trim()) return;
    try {
      const r = await axios.post(`${API}/admin/fraud-report/cases/${c.id}/note`, { text }, cfg);
      patchCase(r.data.case); clear();
    } catch { toast.error("Could not save note"); }
  };
  const delNote = async (c, noteId) => {
    try {
      const r = await axios.delete(`${API}/admin/fraud-report/cases/${c.id}/note/${noteId}`, cfg);
      patchCase(r.data.case);
    } catch { toast.error("Could not delete note"); }
  };
  const toggleTag = async (c, tag) => {
    const next = c.tags.includes(tag) ? c.tags.filter((t) => t !== tag) : [...c.tags, tag];
    try {
      const r = await axios.post(`${API}/admin/fraud-report/cases/${c.id}/tags`, { tags: next }, cfg);
      patchCase(r.data.case);
    } catch { toast.error("Could not update tags"); }
  };
  const addAgency = async (c, entry, clear) => {
    if (!entry.agency.trim()) { toast.error("Pick or type an agency"); return; }
    try {
      const r = await axios.post(`${API}/admin/fraud-report/cases/${c.id}/agency-log`, entry, cfg);
      patchCase(r.data.case); clear();
    } catch { toast.error("Could not log agency filing"); }
  };
  const delAgency = async (c, logId) => {
    try {
      const r = await axios.delete(`${API}/admin/fraud-report/cases/${c.id}/agency-log/${logId}`, cfg);
      patchCase(r.data.case);
    } catch { toast.error("Could not remove entry"); }
  };
  const archive = async (c) => {
    const to = !c.archived;
    if (to && !window.confirm(`Archive the case for ${c.victim_name || c.victim_entity}? It will move off the active board (recoverable from "Show archived").`)) return;
    try {
      const r = await axios.post(`${API}/admin/fraud-report/cases/${c.id}/archive`, { archived: to }, cfg);
      patchCase(r.data.case);
      toast.success(to ? "Case archived" : "Case restored");
    } catch { toast.error("Could not archive"); }
  };

  const downloadKit = async (c) => {
    setBusyId(c.id + ":kit");
    try {
      const r = await axios.get(`${API}/admin/fraud-report/cases/${c.id}/kit`, { ...cfg, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not assemble kit."); }
    finally { setBusyId(null); }
  };

  const resend = async (c) => {
    if (!window.confirm(`Re-assemble and re-send the case kit for ${c.victim_name || c.victim_entity}?\n\nThis emails ${c.victim_email || "the client"}${c.delivery_fax ? ` and faxes ${c.delivery_fax}` : ""}.`)) return;
    setBusyId(c.id + ":resend");
    try {
      const r = await axios.post(`${API}/admin/fraud-report/cases/${c.id}/resend`, {}, cfg);
      setCases((prev) => prev.map((x) => (x.id === c.id ? r.data.case : x)));
      const row = r.data.case;
      toast.success(`Kit re-sent${row.emailed?.length ? ` · emailed ${row.emailed.join(", ")}` : ""}${row.fax ? ` · fax ${row.fax.status}` : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not re-send."); }
    finally { setBusyId(null); }
  };

  const copyPortal = (c) => {
    const link = c.portal_link || `${window.location.origin}/fciu-case/${c.id}?t=${c.case_token}`;
    navigator.clipboard?.writeText(link);
    toast.success("Client portal link copied to clipboard");
  };
  const openPortal = (c) => {
    const link = c.portal_link || `${window.location.origin}/fciu-case/${c.id}?t=${c.case_token}`;
    window.open(link, "_blank");
  };

  const filtered = useMemo(() => {
    let rows = cases;
    if (filter !== "all") rows = rows.filter((c) => (c.status || "intake") === filter);
    const s = q.trim().toLowerCase();
    if (s) rows = rows.filter((c) =>
      [c.victim_name, c.victim_entity, c.victim_email, c.fraud_type, c.case_ref, c.signer_name]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(s)));
    return rows;
  }, [cases, q, filter]);

  const card = { background: "#0A241A", border: "1px solid #14503B", borderRadius: 14 };

  const Stat = ({ label, value, color }) => (
    <div style={{ ...card, padding: "16px 18px", flex: "1 1 150px", minWidth: 150 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#9ec9ba", textTransform: "uppercase", letterSpacing: ".1em" }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: color || "#E7F3EE", marginTop: 4 }}>{value}</div>
    </div>
  );

  return (
    <div style={{ background: "#06160F", minHeight: "100vh", color: "#E7F3EE", fontFamily: "'Manrope',system-ui,sans-serif" }} data-testid="investigations-board">
      <div style={{ maxWidth: 1240, margin: "0 auto", padding: "32px 22px 64px" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24, flexWrap: "wrap" }}>
          <img src="/seutrade_logo.png" alt="SEUTRADE" style={{ width: 52, height: 52, borderRadius: "50%", background: "#fff", padding: 3 }} />
          <div style={{ flex: 1 }}>
            <div style={{ color: "#6FD3AE", fontWeight: 800, letterSpacing: ".08em", fontSize: 12, textTransform: "uppercase" }}>Financial Crimes Investigations Unit</div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>Investigations Board</div>
          </div>
          <button onClick={() => load(true)} data-testid="inv-refresh" disabled={refreshing}
            style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#0A241A", border: "1px solid #14503B", color: "#6FD3AE", padding: "9px 14px", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
            {refreshing ? <Loader2 className="animate-spin" size={15} /> : <RefreshCw size={15} />} Refresh
          </button>
          <button onClick={() => nav("/fciu-flyer")} data-testid="inv-flyer"
            style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#0e2233", border: "1px solid #14503B", color: "#7ec6ff", padding: "9px 14px", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
            <Printer size={15} /> Flyer + QR
          </button>
          <button onClick={() => nav("/fraud-report")} data-testid="inv-new-case"
            style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#6FD3AE", color: "#06160F", padding: "9px 14px", borderRadius: 10, fontWeight: 800, fontSize: 13, cursor: "pointer", border: "none" }}>
            <ShieldAlert size={15} /> New / build packet
          </button>
          <button onClick={() => nav("/admin")} data-testid="inv-back"
            style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: "1px solid #14503B", color: "#9ec9ba", padding: "9px 12px", borderRadius: 10, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
            <ArrowLeft size={14} /> Admin
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 24 }} data-testid="inv-stats">
            <Stat label="Total cases" value={stats.total} />
            <Stat label="Engaged (paid)" value={stats.paid} color="#6FD3AE" />
            <Stat label="Kits delivered" value={stats.delivered} color="#7ec6ff" />
            <Stat label="Awaiting payment" value={stats.intake} color="#E4C97A" />
            <Stat label="Fees collected" value={money(stats.fees_collected)} color="#6FD3AE" />
            <Stat label="Loss reported" value={money(stats.loss_reported)} />
          </div>
        )}

        {/* Controls */}
        <div style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ position: "relative", flex: "1 1 280px", minWidth: 220 }}>
            <Search size={15} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#5e8577" }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client, email, type, reference…" data-testid="inv-search"
              style={{ width: "100%", background: "#0A241A", border: "1px solid #14503B", color: "#E7F3EE", borderRadius: 10, padding: "10px 12px 10px 36px", fontSize: 14, outline: "none" }} />
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {["all", "intake", "engaged", "delivered"].map((k) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`inv-filter-${k}`}
                style={{ background: filter === k ? "#6FD3AE" : "#0A241A", color: filter === k ? "#06160F" : "#9ec9ba", border: "1px solid #14503B", padding: "8px 13px", borderRadius: 9, fontWeight: 700, fontSize: 12, cursor: "pointer", textTransform: "capitalize" }}>
                {k === "all" ? "All" : (STATUS_META[k]?.label || k)}
              </button>
            ))}
          </div>
          <button onClick={() => setShowArchived((v) => !v)} data-testid="inv-toggle-archived"
            style={{ display: "inline-flex", alignItems: "center", gap: 7, background: showArchived ? "#6FD3AE" : "#0A241A", color: showArchived ? "#06160F" : "#9ec9ba", border: "1px solid #14503B", padding: "8px 13px", borderRadius: 9, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
            <Archive size={14} /> {showArchived ? "Showing archived" : "Show archived"}{stats?.archived ? ` (${stats.archived})` : ""}
          </button>
          <button onClick={exportCsv} data-testid="inv-export-csv"
            style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#0e2233", color: "#7ec6ff", border: "1px solid #14503B", padding: "8px 13px", borderRadius: 9, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
            <FileDown size={14} /> Export CSV
          </button>
        </div>

        {/* List */}
        {loading && <div style={{ display: "flex", gap: 10, color: "#9ec9ba", padding: "50px 0" }}><Loader2 className="animate-spin" size={20} /> Loading investigations…</div>}

        {!loading && filtered.length === 0 && (
          <div style={{ ...card, padding: 36, textAlign: "center", color: "#9ec9ba" }} data-testid="inv-empty">
            No cases match. Engagements appear here the moment a client submits the Financial Crimes Unit intake.
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {!loading && filtered.map((c) => {
            const sm = STATUS_META[c.status || "intake"] || STATUS_META.intake;
            const SIcon = sm.icon;
            return (
              <div key={c.id} style={{ ...card, padding: 18 }} data-testid={`inv-case-${c.id}`}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start" }}>
                  {/* Left: identity */}
                  <div style={{ flex: "1 1 260px", minWidth: 220 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                      <span style={{ fontSize: 16, fontWeight: 800 }}>{c.victim_name || c.victim_entity || "Unnamed complainant"}</span>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: sm.bg, color: sm.color, border: `1px solid ${sm.color}55`, padding: "3px 9px", borderRadius: 20, fontSize: 11, fontWeight: 800 }}>
                        <SIcon size={12} /> {sm.label}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: "#9ec9ba", lineHeight: 1.7 }}>
                      {c.victim_email && <div><Mail size={11} style={{ display: "inline", marginRight: 5 }} />{c.victim_email}</div>}
                      {c.fraud_type && <div>Type: <span style={{ color: "#cfe7de" }}>{c.fraud_type}</span></div>}
                      <div>Reported loss: <span style={{ color: "#E4C97A", fontWeight: 700 }}>{money(c.loss_amount)}</span></div>
                      {c.case_ref && <div>Ref: <span style={{ fontFamily: "monospace", color: "#cfe7de" }}>{c.case_ref}</span></div>}
                      <div style={{ color: "#5e8577" }}>Submitted {fmtDate(c.created_at)}</div>
                    </div>
                  </div>

                  {/* Middle: payment + delivery */}
                  <div style={{ flex: "1 1 220px", minWidth: 200, fontSize: 12, color: "#9ec9ba", lineHeight: 1.8 }}>
                    <div><DollarSign size={12} style={{ display: "inline", marginRight: 4 }} />
                      {c.paid
                        ? <span style={{ color: "#6FD3AE", fontWeight: 700 }}>Paid {money(c.fee_paid || c.fee)} · {fmtDate(c.paid_at)}</span>
                        : <span style={{ color: "#E4C97A", fontWeight: 700 }}>Fee {money(c.fee)} not yet paid</span>}
                    </div>
                    <div>
                      {c.delivered
                        ? <span style={{ color: "#7ec6ff" }}><CheckCircle2 size={12} style={{ display: "inline", marginRight: 4 }} />Kit delivered {fmtDate(c.delivered_at)}</span>
                        : <span>Kit not delivered yet</span>}
                    </div>
                    {c.emailed?.length > 0 && <div style={{ color: "#6FD3AE" }}>Emailed: {c.emailed.join(", ")}</div>}
                    {c.fax && <div style={{ color: c.fax.ok ? "#6FD3AE" : "#ff9b86" }}>Fax {c.fax.to}: {c.fax.status}</div>}
                    {c.fulfillment_error && <div style={{ color: "#ff9b86" }}><AlertTriangle size={12} style={{ display: "inline", marginRight: 4 }} />{c.fulfillment_error}</div>}
                    {c.paid && (
                      <div style={{ marginTop: 6 }}>
                        <div style={{ fontSize: 11, color: "#5e8577" }}>Recovery checklist · {c.checklist_done}/{c.checklist_total}</div>
                        <div style={{ height: 6, background: "#063024", borderRadius: 6, overflow: "hidden", marginTop: 3 }}>
                          <div style={{ width: `${(c.checklist_done / c.checklist_total) * 100}%`, height: "100%", background: "#6FD3AE" }} />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right: actions */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 7, minWidth: 160 }}>
                    <button onClick={() => downloadKit(c)} disabled={busyId === c.id + ":kit"} data-testid={`inv-kit-${c.id}`}
                      style={btn("#0e2233", "#7ec6ff")}>
                      {busyId === c.id + ":kit" ? <Loader2 className="animate-spin" size={13} /> : <Printer size={13} />} View kit PDF
                    </button>
                    {c.paid && (
                      <button onClick={() => resend(c)} disabled={busyId === c.id + ":resend"} data-testid={`inv-resend-${c.id}`}
                        style={btn("#0A241A", "#6FD3AE")}>
                        {busyId === c.id + ":resend" ? <Loader2 className="animate-spin" size={13} /> : <RefreshCw size={13} />} Re-send kit
                      </button>
                    )}
                    {c.paid && c.case_token && (
                      <>
                        <button onClick={() => openPortal(c)} data-testid={`inv-portal-${c.id}`} style={btn("#0A241A", "#E4C97A")}>
                          <ExternalLink size={13} /> Open portal
                        </button>
                        <button onClick={() => copyPortal(c)} data-testid={`inv-copylink-${c.id}`} style={btn("transparent", "#9ec9ba")}>
                          <Link2 size={13} /> Copy link
                        </button>
                      </>
                    )}
                    <button onClick={() => setExpanded(expanded === c.id ? null : c.id)} data-testid={`inv-manage-${c.id}`} style={btn("transparent", "#9ec9ba")}>
                      {expanded === c.id ? <ChevronUp size={13} /> : <ChevronDown size={13} />} Manage
                    </button>
                    <button onClick={() => archive(c)} data-testid={`inv-archive-${c.id}`} style={btn("transparent", c.archived ? "#6FD3AE" : "#ff9b86")}>
                      {c.archived ? <ArchiveRestore size={13} /> : <Archive size={13} />} {c.archived ? "Restore" : "Archive"}
                    </button>
                  </div>
                </div>

                {/* Tag chips (always visible when present) */}
                {c.tags?.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 12 }} data-testid={`inv-tags-${c.id}`}>
                    {c.tags.map((t) => (
                      <span key={t} style={{ fontSize: 11, fontWeight: 700, color: TAG_COLOR[t] || "#9ec9ba", background: "#06160F", border: `1px solid ${(TAG_COLOR[t] || "#9ec9ba")}55`, padding: "3px 9px", borderRadius: 20 }}>{t}</span>
                    ))}
                  </div>
                )}

                {expanded === c.id && (
                  <ManagePanel c={c} onAddNote={addNote} onDelNote={delNote} onToggleTag={toggleTag} onAddAgency={addAgency} onDelAgency={delAgency} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ManagePanel({ c, onAddNote, onDelNote, onToggleTag, onAddAgency, onDelAgency }) {
  const [note, setNote] = useState("");
  const [ag, setAg] = useState({ agency: "", reference: "", filed_at: "" });
  const sub = { fontSize: 11, fontWeight: 800, color: "#6FD3AE", textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 8 };
  const inp = { background: "#06160F", border: "1px solid #14503B", color: "#E7F3EE", borderRadius: 8, padding: "8px 10px", fontSize: 13, outline: "none", width: "100%" };
  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #14503B", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }} data-testid={`inv-manage-panel-${c.id}`}>
      {/* Tags */}
      <div style={{ gridColumn: "1 / -1" }}>
        <div style={sub}><Tag size={12} style={{ display: "inline", marginRight: 5 }} />Status tags</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {PRESET_TAGS.map((t) => {
            const on = c.tags.includes(t);
            return (
              <button key={t} onClick={() => onToggleTag(c, t)} data-testid={`inv-tag-${c.id}-${t.replace(/\s+/g, "-").toLowerCase()}`}
                style={{ fontSize: 11, fontWeight: 700, color: on ? "#06160F" : (TAG_COLOR[t] || "#9ec9ba"), background: on ? (TAG_COLOR[t] || "#9ec9ba") : "transparent", border: `1px solid ${(TAG_COLOR[t] || "#9ec9ba")}66`, padding: "5px 11px", borderRadius: 20, cursor: "pointer" }}>
                {t}
              </button>
            );
          })}
        </div>
      </div>

      {/* Notes */}
      <div>
        <div style={sub}><StickyNote size={12} style={{ display: "inline", marginRight: 5 }} />Investigator notes</div>
        <div style={{ display: "flex", gap: 7, marginBottom: 10 }}>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a private note…" data-testid={`inv-note-input-${c.id}`}
            onKeyDown={(e) => { if (e.key === "Enter") onAddNote(c, note, () => setNote("")); }} style={inp} />
          <button onClick={() => onAddNote(c, note, () => setNote(""))} data-testid={`inv-note-add-${c.id}`} style={{ ...btn("#6FD3AE", "#06160F"), border: "none" }}><Plus size={14} /></button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {(c.admin_notes || []).length === 0 && <div style={{ fontSize: 12, color: "#5e8577" }}>No notes yet.</div>}
          {(c.admin_notes || []).map((n) => (
            <div key={n.id} style={{ background: "#06160F", border: "1px solid #0f3b2b", borderRadius: 8, padding: "8px 10px" }} data-testid={`inv-note-${n.id}`}>
              <div style={{ fontSize: 13, color: "#E7F3EE", whiteSpace: "pre-wrap" }}>{n.text}</div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 10, color: "#5e8577" }}>
                <span>{n.by || "admin"} · {fmtDate(n.at)}</span>
                <button onClick={() => onDelNote(c, n.id)} data-testid={`inv-note-del-${n.id}`} style={{ background: "none", border: "none", color: "#ff9b86", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10 }}><Trash2 size={11} /> delete</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Agency submission log */}
      <div>
        <div style={sub}><Landmark size={12} style={{ display: "inline", marginRight: 5 }} />Agency submission log</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 7, marginBottom: 10 }}>
          <select value={ag.agency} onChange={(e) => setAg({ ...ag, agency: e.target.value })} data-testid={`inv-agency-select-${c.id}`} style={inp}>
            <option value="">Select agency…</option>
            {PRESET_AGENCIES.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <div style={{ display: "flex", gap: 7 }}>
            <input value={ag.reference} onChange={(e) => setAg({ ...ag, reference: e.target.value })} placeholder="Reference / complaint #" data-testid={`inv-agency-ref-${c.id}`} style={inp} />
            <input type="date" value={ag.filed_at} onChange={(e) => setAg({ ...ag, filed_at: e.target.value })} data-testid={`inv-agency-date-${c.id}`} style={{ ...inp, width: 150 }} />
            <button onClick={() => onAddAgency(c, ag, () => setAg({ agency: "", reference: "", filed_at: "" }))} data-testid={`inv-agency-add-${c.id}`} style={{ ...btn("#6FD3AE", "#06160F"), border: "none" }}><Send size={14} /></button>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {(c.agency_log || []).length === 0 && <div style={{ fontSize: 12, color: "#5e8577" }}>No filings logged yet.</div>}
          {(c.agency_log || []).map((a) => (
            <div key={a.id} style={{ background: "#06160F", border: "1px solid #0f3b2b", borderRadius: 8, padding: "8px 10px" }} data-testid={`inv-agency-${a.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: 13, color: "#E7F3EE", fontWeight: 700 }}>{a.agency}</div>
                <button onClick={() => onDelAgency(c, a.id)} data-testid={`inv-agency-del-${a.id}`} style={{ background: "none", border: "none", color: "#ff9b86", cursor: "pointer" }}><Trash2 size={12} /></button>
              </div>
              <div style={{ fontSize: 11, color: "#9ec9ba" }}>{a.reference ? `Ref: ${a.reference} · ` : ""}Filed {a.filed_at}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const btn = (bg, color) => ({
  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7,
  background: bg, color, border: `1px solid ${color}44`, padding: "8px 12px",
  borderRadius: 9, fontWeight: 700, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap",
});
